import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { preReleaseService, PreReleaseProject } from '../../services/preReleaseService';
import PreReleaseSetupStatus from '../../components/prerelease/PreReleaseSetupStatus';
import { showToast } from '../../utils/toast';
import '../../styles/pages/PublisherDashboard.css';
import '../../styles/components/prerelease/PreReleaseSetupStatus.css';

export const PublisherDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [projects, setProjects] = useState<PreReleaseProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const [showInsufficientPoints, setShowInsufficientPoints] = useState(false);

  // New state for search and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title' | 'status' | 'interest'>('newest');

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  // Filter and sort projects with error handling
  const filteredAndSortedProjects = useMemo(() => {
    try {
      return (projects || [])
        .filter(project => {
          // Safety check for project object
          if (!project || typeof project !== 'object') return false;

          // Safety check for searchTerm
          if (!searchTerm || typeof searchTerm !== 'string' || !searchTerm.trim()) return true;

          const searchLower = searchTerm.toLowerCase();
          return (
            (project?.title && typeof project.title === 'string' && project.title.toLowerCase().includes(searchLower)) ||
            (project?.description && typeof project.description === 'string' && project.description.toLowerCase().includes(searchLower)) ||
            (project?.status && typeof project.status === 'string' && project.status.toLowerCase().includes(searchLower)) ||
            (project?.project_category && typeof project.project_category === 'string' && project.project_category.toLowerCase().includes(searchLower))
          );
        })
        .sort((a, b) => {
          // Safety checks for sort objects
          if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return 0;

          switch (sortBy) {
            case 'newest':
              const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
              const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
              return dateB - dateA;
            case 'oldest':
              const dateA2 = a.created_at ? new Date(a.created_at).getTime() : 0;
              const dateB2 = b.created_at ? new Date(b.created_at).getTime() : 0;
              return dateA2 - dateB2;
            case 'title':
              const titleA = a.title && typeof a.title === 'string' ? a.title : '';
              const titleB = b.title && typeof b.title === 'string' ? b.title : '';
              return titleA.localeCompare(titleB);
            case 'status':
              const statusA = a.status && typeof a.status === 'string' ? a.status : '';
              const statusB = b.status && typeof b.status === 'string' ? b.status : '';
              return statusA.localeCompare(statusB);
            case 'interest':
              const interestA = typeof a.interest_count === 'number' ? a.interest_count : 0;
              const interestB = typeof b.interest_count === 'number' ? b.interest_count : 0;
              return interestB - interestA;
            default:
              return 0;
          }
        });
    } catch (error) {
      console.error('Error filtering/sorting projects:', error);
      return projects || [];
    }
  }, [projects, searchTerm, sortBy]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSetupRequired(false);

      // Check if setup is required by monitoring console warnings
      const originalConsoleWarn = console.warn;
      let setupWarningDetected = false;
      console.warn = (...args) => {
        if (args[0]?.includes('Pre-release functions not found') || args[0]?.includes('Pre-release tables not found')) {
          setupWarningDetected = true;
        }
        originalConsoleWarn(...args);
      };

      // Pass user.id to avoid redundant User API call
      const data = await preReleaseService.getPublisherDashboard(user?.id);

      // Restore original console.warn
      console.warn = originalConsoleWarn;

      if (setupWarningDetected) {
        setSetupRequired(true);
      } else {
        setDashboardData(data);
        // Ensure projects is always an array with valid objects
        const projectsArray = Array.isArray(data?.projects) ? data.projects.filter((p: any) => p && typeof p === 'object') : [];
        console.log('Loaded projects:', projectsArray.length, 'valid projects from data:', data?.projects?.length);
        setProjects(projectsArray);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setSetupRequired(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    if (!user || user.points < 100) {
      setShowInsufficientPoints(true);
      return;
    }
    navigate('/create-pre-release-project');
  };

  const handleReleaseProject = async (projectId: string) => {
    if (!window.confirm(t('publisherDashboard.confirmRelease'))) {
      return;
    }

    try {
      const notificationCount = await preReleaseService.releaseProject(projectId);
      showToast(t('publisherDashboard.releaseSuccess', { count: notificationCount }), 'success');
      loadDashboardData();
    } catch (err) {
      console.error('Error releasing project:', err);
      showToast(t('publisherDashboard.releaseFailed'), 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'concept': return '#ffc107';
      case 'development': return '#007bff';
      case 'released': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusTranslation = (status: string) => {
    switch (status) {
      case 'concept': return t('publisherDashboard.statusConcept');
      case 'development': return t('publisherDashboard.statusDevelopment');
      case 'released': return t('publisherDashboard.statusReleased');
      case 'cancelled': return t('publisherDashboard.statusCancelled');
      default: return t('publisherDashboard.unknownStatus');
    }
  };

  if (!user) {
    return (
      <div className="publisher-dashboard">
        <div className="auth-required">
          <h2>{t('publisherDashboard.authRequired')}</h2>
          <p>{t('publisherDashboard.pleaseLogin')} <Link to="/login">{t('publisherDashboard.login')}</Link> {t('publisherDashboard.toAccessDashboard')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-ring"></div>
        <p>{t('publisherDashboard.loading')}</p>
      </div>
    );
  }

  // Show setup status if database setup is required
  if (setupRequired) {
    return <PreReleaseSetupStatus onRetry={loadDashboardData} />;
  }

  if (error) {
    return (
      <div className="publisher-dashboard">
        <div className="error-message">
          <h2>{t('publisherDashboard.error')}</h2>
          <p>{error}</p>
          <button onClick={loadDashboardData} className="retry-btn">
            {t('publisherDashboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="publisher-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="dashboard-hero-centered">
            <div className="dashboard-icon-wrapper">
              <div className="dashboard-icon-large">
                <span className="dashboard-icon-placeholder">🚀</span>
              </div>
            </div>

            <div className="dashboard-info">
              <div className="dashboard-title-group">
                <h1 className="dashboard-title-main">{t('publisherDashboard.title')}</h1>
                <div className="dashboard-badge" onClick={handleCreateProject}>
                  <i className="bi bi-rocket"></i>
                  {t('publisherDashboard.launchReady')}
                </div>
              </div>
              <p className="dashboard-description">
                {t('publisherDashboard.description')}
              </p>

              <div className="dashboard-meta">
                <div className="meta-item">
                  <i className="bi bi-briefcase"></i>
                  <span>{t('publisherDashboard.activeProjectsCount', { count: projects.length })}</span>
                </div>
                <div className="meta-item">
                  <i className="bi bi-people"></i>
                  <span>{t('publisherDashboard.totalInterestCount', { count: dashboardData?.total_interest || 0 })}</span>
                </div>
                <div className="meta-item">
                  <i className="bi bi-cart"></i>
                  <span>{t('publisherDashboard.willPurchaseCount', { count: dashboardData?.total_willing_to_pay || 0 })}</span>
                </div>
              </div>

              <div className="dashboard-actions">
                {user && user.points < 100 && (
                  <div className="points-requirement">
                    {t('publisherDashboard.pointsRequirement', { required: 100, current: user.points })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-wide-container">
        <div className="dashboard-main-content">
          {dashboardData && (
            <div className="dashboard-stats">
              <div className="stat-card">
                <div className="stat-number">{dashboardData.total_projects}</div>
                <div className="stat-label">{t('publisherDashboard.totalProjectsLabel', { count: dashboardData.total_projects })}</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{dashboardData.active_projects}</div>
                <div className="stat-label">{t('publisherDashboard.activeProjectsLabel', { count: dashboardData.active_projects })}</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{dashboardData.total_interest}</div>
                <div className="stat-label">{t('publisherDashboard.totalInterestLabel', { count: dashboardData.total_interest })}</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{dashboardData.total_willing_to_pay}</div>
                <div className="stat-label">{t('publisherDashboard.willingToPurchaseLabel', { count: dashboardData.total_willing_to_pay })}</div>
              </div>
            </div>
          )}

          <div className="projects-section">
            <div className="projects-section-header">
              <h2>{t('publisherDashboard.yourProjects')}</h2>

              {/* Search and Filter Controls */}
              <div className="projects-controls">
                <div className="search-bar">
                  <i className="bi bi-search search-icon"></i>
                  <input
                    type="text"
                    placeholder={t('publisherDashboard.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm('')}
                      title={t('publisherDashboard.clearSearch')}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>

                <div className="sort-controls">
                  <label htmlFor="sort-select">{t('publisherDashboard.sortBy')}</label>
                  <select
                    id="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="sort-select"
                  >
                    <option value="newest">{t('publisherDashboard.newestFirst')}</option>
                    <option value="oldest">{t('publisherDashboard.oldestFirst')}</option>
                    <option value="title">{t('publisherDashboard.titleAZ')}</option>
                    <option value="status">{t('publisherDashboard.status')}</option>
                    <option value="interest">{t('publisherDashboard.mostInterest')}</option>
                  </select>
                </div>
              </div>

              {/* Results count */}
              {searchTerm && (
                <div className="search-results-info">
                  {t('publisherDashboard.searchResults', { found: filteredAndSortedProjects.length, total: projects.length })}
                </div>
              )}
            </div>

            {filteredAndSortedProjects.length === 0 ? (
              searchTerm ? (
                <div className="no-results">
                  <div className="no-results-content">
                    <i className="bi bi-search no-results-icon"></i>
                    <h3>{t('publisherDashboard.noProjectsFound')}</h3>
                    <p>{t('publisherDashboard.noProjectsFoundDesc')}</p>
                    <button
                      className="clear-search-btn"
                      onClick={() => setSearchTerm('')}
                    >
                      {t('publisherDashboard.clearSearch')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="no-projects">
                  <div className="no-projects-content">
                    <h3>{t('publisherDashboard.noProjectsYet')}</h3>
                    <p>{t('publisherDashboard.noProjectsYetDesc')}</p>
                    <button
                      className="create-first-project-btn"
                      onClick={handleCreateProject}
                    >
                      {t('publisherDashboard.createFirstProject')}
                    </button>
                  </div>
                </div>
              )
            ) : (
              <div className="projects-grid">
                {filteredAndSortedProjects.map(project => (
                  <div key={project.id} className="project-card">
                    <div className="project-header">
                      <div className="project-title">
                        <Link to={`/pre-release/${project.id}`}>
                          {project.title || t('publisherDashboard.untitledProject')}
                        </Link>
                      </div>
                      <div
                        className="project-status"
                        style={{ backgroundColor: getStatusColor(project.status || 'unknown') }}
                      >
                        {getStatusTranslation(project.status || 'unknown')}
                      </div>
                    </div>

                    <div className="project-description">
                      {project.description || t('publisherDashboard.noDescription')}
                    </div>

                    <div className="project-stats">
                      <div className="stat">
                        <span className="stat-number">{project.interest_count || 0}</span>
                        <span className="stat-label">{t('publisherDashboard.interested')}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-number">{project.willing_to_pay_count || 0}</span>
                        <span className="stat-label">{t('publisherDashboard.willBuy')}</span>
                      </div>
                    </div>

                    <div className="project-meta">
                      <div className="created-date">
                        {t('publisherDashboard.created')}: {project.created_at ? new Date(project.created_at).toLocaleDateString() : t('publisherDashboard.unknownDate')}
                      </div>
                      {project.proposed_price && (
                        <div className="price">
                          ${project.proposed_price.toFixed(2)}
                        </div>
                      )}
                    </div>

                    <div className="project-actions">
                      <div className="primary-actions">
                        <Link
                          to={`/pre-release/${project.id}`}
                          className="view-btn"
                        >
                          <i className="bi bi-eye"></i>
                          {t('publisherDashboard.viewDetails')}
                        </Link>

                        {project.status !== 'cancelled' && (
                          <button
                            className="edit-btn"
                            onClick={() => navigate(`/edit-pre-release-project/${project.id}`)}
                            title={t('publisherDashboard.editProject')}
                          >
                            <i className="bi bi-pencil"></i>
                            {t('publisherDashboard.edit')}
                          </button>
                        )}
                      </div>

                      {project.status === 'concept' && (
                        <div className="secondary-actions">
                          <button
                            className="release-btn"
                            onClick={() => handleReleaseProject(project.id)}
                          >
                            <i className="bi bi-rocket-takeoff"></i>
                            {t('publisherDashboard.markAsReleased')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showInsufficientPoints && (
        <div className="modal-overlay" onClick={() => setShowInsufficientPoints(false)}>
          <div className="insufficient-points-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('publisherDashboard.insufficientPoints')}</h3>
              <button className="close-btn" onClick={() => setShowInsufficientPoints(false)}>×</button>
            </div>
            <div className="modal-content">
              <p>{t('publisherDashboard.needPoints', { points: 100 })}</p>
              <p>{t('publisherDashboard.currentPoints', { points: user?.points || 0 })}</p>
              <p>{t('publisherDashboard.earnMorePoints')}</p>
            </div>
            <div className="modal-actions">
              <button
                className="close-modal-btn"
                onClick={() => setShowInsufficientPoints(false)}
              >
                {t('publisherDashboard.gotIt')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
